import { Subject } from "rxjs"
import HttpResponse from "./models/http-response"

/**
 * All HTTP functionality.
 * 
 * @author Stan Hurks
 */
export default class Http {
    public static readonly onAPINotReachable: Subject<void> = new Subject()

    public static readonly onHttpStatusUnauthorized: Subject<void> = new Subject()

    public static readonly onHttpStatusPaymentRequired: Subject<void> = new Subject()
    
    public static readonly onHttpStatusInternalServerError: Subject<void> = new Subject()

    /**
     * The HTTP options
     */
    public static options = {
        apiBase: 'https://x5vu7axuq1.execute-api.eu-central-1.amazonaws.com/production/'
    }

    /**
     * Makes a get request
     * @param path the path to the resource
     * @param headers the object of request headers
     */
    public static get = <T = any>(path: string, headers: {[key: string]: string} = Http.generateHeaders()): Promise<HttpResponse<T>> => {
        return Http.request('GET', Http.options.apiBase + path, {}, headers || {})
    }

    /**
     * Makes a post request
     * @param path the path to the resource
     * @param body the request body
     * @param headers the object of request headers
     */
    public static post = <T = any>(
        path: string, body: {[key: string]: any}, 
        headers: {[key: string]: string} = Http.generateHeaders()
    ): Promise<HttpResponse<T>> => {

        return Http.request('POST', Http.options.apiBase + path, body || {}, headers || {})
    }

    /**
     * Makes a put request
     * @param path the path to the resource
     * @param body the request body
     * @param headers the object of request headers
     */
    public static put = <T = any>(
        path: string, body: {[key: string]: any}, 
        headers: {[key: string]: string} = Http.generateHeaders()
    ): Promise<HttpResponse<T>> => {

        return Http.request('PUT', Http.options.apiBase + path, body || {}, headers || {})
    }

    /**
     * Makes a delete request
     * @param path the path to the resource
     * @param body the request body
     * @param headers the object of request headers
     */
    public static delete = <T = any>(
        path: string, body: {[key: string]: any}, 
        headers: {[key: string]: string} = Http.generateHeaders()
    ): Promise<HttpResponse<T>> => {

        return Http.request('DELETE', Http.options.apiBase + path, body || {}, headers || {})
    }

    /**
     * Makes a XMLHttpRequest and return the response as a promise.
     * @param method the request method
     * @param path the path to the resource
     * @param body the request body
     * @param headers the object of request headers
     */
    public static request = <T = any>(
        method: string, path: string, body: {[key: string]: any}, 
        headers: {[key: string]: string} = Http.generateHeaders()
    ): Promise<HttpResponse<T>> => {

        return new Promise((resolve, reject) => {
            const request = new XMLHttpRequest()
            request.open(method, path)
            request.setRequestHeader('Content-Type', 'application/json')
            if (headers != null) {
                Object.keys(headers).forEach((key) => {
                    request.setRequestHeader(key, headers[key])
                })
            }
            request.onload = () => {
                if (request.status >= 200 && request.status < 400) {
                    let response: any
                    try {
                        response = JSON.parse(request.response)
                    } catch (e) {
                        console.error(e)
                        response = request.response
                    }
                    resolve({
                        data: response,
                        status: request.status
                    })
                } else {
                    if (request.status === 401) {
                        Http.onHttpStatusUnauthorized.next()
                    } else if (request.status === 402) {
                        Http.onHttpStatusPaymentRequired.next()
                    } else if (request.status === 500) {
                        Http.onHttpStatusInternalServerError.next()
                    }

                    let response: any
                    try {
                        response = JSON.parse(request.response)
                    } catch (e) {
                        response = request.response
                    }

                    // eslint-disable-next-line
                    reject({
                        data: response,
                        status: request.status
                    })
                }
            }
            request.onerror = () => {
                Http.onAPINotReachable.next()
                reject(request.statusText)
            }
            request.send(JSON.stringify(body))
        })
    }

    /**
     * Generate the headers for a http request
     */
    public static generateHeaders = (): any => {
        return {
            Authorization: localStorage.getItem('sessionId') || sessionStorage.getItem('sessionId')
        }
    }
}